import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Persistent storage utility that uses Capacitor Preferences on native platforms
 * and localStorage on web. This ensures data persists across app restarts on mobile.
 */
class PersistentStorage {
  /**
   * Check if running on native platform
   */
  isNative() {
    return Capacitor.isNativePlatform();
  }

  /**
   * Set a value in storage
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   */
  async set(key, value) {
    // Guard the storing-null-as-string-"null" footgun. localStorage.setItem
    // and Capacitor Preferences both coerce non-string values, so passing
    // null/undefined stores the literal "null" / "undefined" strings — which
    // every consumer that does `value || fallback` then treats as truthy.
    // This produced the bug where customers with no email logged in via
    // phone and then ProfileBookingHistory called /bookings/by-phone?email=null.
    if (value === null || value === undefined) {
      // Surface "we tried to store nothing" — it usually means a login API
      // response was missing a field we expected (e.g. user.user_id).
      // Silent removal was hiding the real bug.
      console.warn(`[persistentStorage] set('${key}', ${value}) — removing key`);
      return this.remove(key);
    }
    // Capacitor Preferences on Android requires `value: string`. The JNI
    // bridge does `call.getString("value")`, which returns null for a JS
    // Number (e.g. user.user_id = 45) — silently storing null. Coerce here
    // so numbers, booleans, etc. survive the round-trip.
    const stringValue = typeof value === 'string' ? value : String(value);
    if (this.isNative()) {
      // Use Capacitor Preferences for native platforms (persists across app restarts)
      await Preferences.set({ key, value: stringValue });
      // Write-through to localStorage so the app's many synchronous
      // localStorage.getItem(...) reads stay correct within the session. The
      // WebView's localStorage is wiped on native relaunch; hydrate() restores
      // it from Preferences at boot, and this keeps it current afterwards.
      try { localStorage.setItem(key, stringValue); } catch (_) { /* WebView quota/availability */ }
    } else {
      // Use localStorage for web
      localStorage.setItem(key, stringValue);
    }
  }

  /**
   * Get a value from storage
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  async get(key) {
    if (this.isNative()) {
      // Use Capacitor Preferences for native platforms
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      // Use localStorage for web
      return localStorage.getItem(key);
    }
  }

  /**
   * Remove a value from storage
   * @param {string} key - Storage key
   */
  async remove(key) {
    if (this.isNative()) {
      // Use Capacitor Preferences for native platforms
      await Preferences.remove({ key });
      // Mirror the removal to the synchronous localStorage mirror (see set()).
      try { localStorage.removeItem(key); } catch (_) { /* WebView availability */ }
    } else {
      // Use localStorage for web
      localStorage.removeItem(key);
    }
  }

  /**
   * Clear all storage
   */
  async clear() {
    if (this.isNative()) {
      // Use Capacitor Preferences for native platforms
      await Preferences.clear();
      // Keep the synchronous mirror in lockstep so a logout fully clears both.
      try { localStorage.clear(); } catch (_) { /* WebView availability */ }
    } else {
      // Use localStorage for web
      localStorage.clear();
    }
  }

  /**
   * Native only: mirror all Capacitor Preferences into localStorage so the
   * app's synchronous localStorage.getItem(...) reads work after an app
   * restart. The WebView's localStorage does NOT survive a native relaunch,
   * but Preferences does — without this, every synchronous session/identity
   * read comes back null on reopen and the dashboards show "Session Expired".
   * Call once at boot, before rendering. No-op on web.
   */
  async hydrate() {
    if (!this.isNative()) return;
    try {
      const { keys } = await Preferences.keys();
      for (const key of keys) {
        const { value } = await Preferences.get({ key });
        if (value !== null && value !== undefined) {
          try { localStorage.setItem(key, value); } catch (_) { /* WebView quota */ }
        }
      }
    } catch (e) {
      console.warn('[persistentStorage] hydrate failed:', e);
    }
  }

  /**
   * Get all keys from storage
   * @returns {string[]} - Array of keys
   */
  async keys() {
    if (this.isNative()) {
      // Use Capacitor Preferences for native platforms
      const { keys } = await Preferences.keys();
      return keys;
    } else {
      // Use localStorage for web
      return Object.keys(localStorage);
    }
  }

  /**
   * Synchronous get for compatibility (use with caution on native)
   * This will only work on web. On native, returns null and logs a warning.
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  getSync(key) {
    // localStorage is kept as a synchronous mirror of Preferences on native
    // (hydrated at boot + write-through on set/remove/clear), so reading it
    // here is now correct on native as well as web.
    return localStorage.getItem(key);
  }

  /**
   * Synchronous set for compatibility (use with caution on native)
   * This will only work on web. On native, logs a warning.
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   */
  setSync(key, value) {
    const stringValue = typeof value === 'string' ? value : String(value);
    // Write the synchronous mirror first so an immediately-following getSync
    // sees the value, then persist to the durable store on native.
    try { localStorage.setItem(key, stringValue); } catch (_) { /* WebView quota */ }
    if (this.isNative()) {
      Preferences.set({ key, value: stringValue }).catch(console.error);
    }
  }

  /**
   * Synchronous remove for compatibility (use with caution on native)
   * This will only work on web. On native, logs a warning.
   * @param {string} key - Storage key
   */
  removeSync(key) {
    try { localStorage.removeItem(key); } catch (_) { /* WebView availability */ }
    if (this.isNative()) {
      Preferences.remove({ key }).catch(console.error);
    }
  }
}

// Export singleton instance
export const persistentStorage = new PersistentStorage();

// Export class for testing
export default PersistentStorage;

